import type { ApolloError } from '@apollo/client';
import * as React from 'react';
import classNames from 'classnames';
import {
  Button,
  DonutChart,
  Heading,
  Surface,
  Text,
} from '@quality24/design-system';
import { faRefresh } from '@fortawesome/pro-regular-svg-icons';

import { DateFilter, DonutChartComponentData } from '../../../../@types';

import { createReferenceMap } from '../../utils/reference';
import { mapData } from '../../utils/mapper';
import { filterData } from '../../utils/filter';
import { formatNumber, parseDataValue } from '../../../../utils';
import { formatChartData } from './utils';

import styles from '../../DataViewContainer.module.scss';

export interface Props extends React.HTMLAttributes<HTMLElement> {
  loading?: boolean;
  error?: ApolloError;
  refetch: () => void;
  data: Record<string, unknown>;
  dateFilter: DateFilter;
  renderOnSurface?: boolean;
  config: Omit<DonutChartComponentData, 'type'>;
}

/**
 * <DonutChartContainer> page
 */
export const DonutChartContainer: React.FunctionComponent<Props> = ({
  className,
  loading,
  error,
  refetch,
  data,
  dateFilter,
  renderOnSurface = true,
  config: {
    title,
    targetKey,
    subtitlePrefix,
    rotate,
    fontSize,
    valueColor,
    className: configClassName,
    rootKey,
    options,
    postQuery,
    references,
    aggrKey,
    groupBy,
    surfaceClassName,
  },
  ...htmlElementProps
}) => {
  /**
   * Gets the aggregation data for this container
   */
  const aggrData = React.useMemo(
    () => filterData(data, rootKey, postQuery),
    [data, rootKey, postQuery],
  );

  /**
   * Creates the reference map
   */
  const referenceMap = React.useMemo(
    () => createReferenceMap({ data, references, dateFilter, postQuery }),
    [data, references, dateFilter, postQuery],
  );

  /**
   * Maps data
   */
  const mappedData = React.useMemo(
    () =>
      mapData({
        data: aggrData,
        groupBy,
        aggrKey,
        reference: referenceMap,
      }) as Array<Record<string, string | number>>,
    [aggrData, groupBy, aggrKey, referenceMap],
  );

  /**
   * Post-processing - Fetch target values
   */
  const targetValue = React.useMemo(() => {
    if (!targetKey) return undefined;
    return Number(parseDataValue(data, targetKey, 'string'));
  }, [data, targetKey]);

  /**
   * Post-processing - Calculate total/remaining
   */
  const chartData = React.useMemo(
    () => formatChartData(mappedData, targetValue),
    [mappedData, targetValue],
  );

  const resolvedValueColor = React.useMemo(() => {
    if (
      targetValue &&
      targetValue > 0 &&
      chartData[0].value >= targetValue &&
      valueColor?.targetReached
    ) {
      return valueColor.targetReached;
    }
    return valueColor?.default;
  }, [chartData, targetValue, valueColor]);

  const subtitle = React.useMemo(() => {
    if (targetValue === undefined) {
      return undefined;
    }
    return subtitlePrefix
      ? `${subtitlePrefix} ${formatNumber(targetValue)}`
      : `${formatNumber(targetValue)}`;
  }, [subtitlePrefix, targetValue]);

  // Add log for debuging with DVC-Designer
  if (process.env.REACT_APP_HOST_PROJECT === 'designer') {
    // eslint-disable-next-line no-console
    console.info('DonutChartContainer', {
      originalData: data,
      aggregatedData: aggrData,
      referenceMap,
      mappedData,
      chartData,
      targetValue,
    });
  }

  return (
    <div
      className={classNames(className, configClassName)}
      {...htmlElementProps}
    >
      <Surface
        className={classNames(styles.surface, surfaceClassName, {
          'on-surface': renderOnSurface,
        })}
        padding="sm"
        backgroundColor="grayLight2"
      >
        {title && (
          <Heading as="h3" size="sm1" weight="bold" className="mb-4">
            {!loading && parseDataValue(data, title, 'string')}
          </Heading>
        )}

        {!loading && !error && aggrData.length === 0 && (
          <Text size="sm2" color="gray100">
            Não há dados disponíveis.
          </Text>
        )}

        {!loading && error && (
          <div className={styles['error-container']}>
            <Text className="mb-2" size="sm2" color="gray100">
              Não foi possível carregar os dados.
            </Text>
            <Button
              variant="outline"
              icon={faRefresh}
              onClick={() => refetch()}
            >
              Recarregar
            </Button>
          </div>
        )}

        {!error && (
          <DonutChart
            loading={loading}
            data={chartData}
            rotate={rotate}
            fontSize={fontSize}
            options={options}
            height={options?.height}
            valueColor={resolvedValueColor}
            value={chartData[0].value}
            subtitle={subtitle}
          />
        )}
      </Surface>
    </div>
  );
};

export default DonutChartContainer;
